import { queryClient } from '@api/clientProvider';
import RightArrow from '@atoms/RightArrow';
import { Text, View } from '@components/primitives';
import config from '@config';
import { Ionicons } from '@expo/vector-icons';
import { isValidNumberWithDot } from '@molecules/SliderView/sliderUtils';
import ScreenNames from '@navigation/navigationConstants';
import { CommonActions } from '@react-navigation/native';
import appStyles from '@root/appStyles';
import colors from '@theme/colors';
import { showErrorToast, showSuccessToast, showWarningToast } from '@utils/toastManager';
import React from 'react';
import { Linking, TouchableOpacity } from 'react-native';
import AmplitudeHelper from './analytics';
import { AMPLITUDE_CONSTANTS, AMPLITUDE_EVENT_SOURCES } from './analytics/constants';
import packageJson from '../../package.json';
import {
  LOGIN_INFO_SHEET_CONTENT,
  LOGIN_INFO_SHEET_KEYS,
  PHONE_ENROLLMENT_STATUS,
  REDIRECTION_TYPES,
  UPDATE_TYPE,
} from '../constants';

export const removeNonNumeric = (num?: string | number): string => num?.toString().replace(/[^0-9]/g, '') ?? '';

export const formatPhoneNumber = (phoneNumber: string, countryCode = '62'): string => {
  if (phoneNumber === '') return '';
  const firstTwoChar = phoneNumber.substring(0, 2);
  const firstChar = phoneNumber.substring(0, 1);

  if (firstTwoChar === '62') {
    return `${countryCode}${phoneNumber.substring(2)}`;
  }

  if (firstChar === '0') {
    return `${countryCode}${phoneNumber.substring(1)}`;
  }

  return `${countryCode}${phoneNumber}`;
};

export const returnBottomSheetContent = (
  key: string,
  content: Record<string, any>,
  phoneNumber: string = '',
): Record<string, any> => {
  const isOtpKey = key === LOGIN_INFO_SHEET_KEYS['GG-OTP-07'] || key === LOGIN_INFO_SHEET_KEYS.RESEND_OTP;

  return {
    ...content,
    title: content?.title,
    phoneNumber: isOtpKey ? phoneNumber : '',
    description: content?.description || null,
    description2: content?.description2 || null,
  };
};

export function gotBackToLogin(navigation: any): void {
  navigation.dispatch(
    CommonActions.reset({
      index: 0,
      routes: [{ name: 'Login' }],
    }),
  );
}

export function resetToHomeNavStack(navigation: any): void {
  navigation.dispatch(
    CommonActions.reset({
      index: 0,
      routes: [{ name: ScreenNames.HOME }],
    }),
  );
}

export function resetToNavStack(navigation: any, screenName: string): void {
  navigation.dispatch(
    CommonActions.reset({
      index: 0,
      routes: [{ name: ScreenNames.HOME }, { name: screenName }],
    }),
  );
}

export function goBackToHome(navigation: any, invalidateHomePageQueries = true): any {
  if (invalidateHomePageQueries) {
    return queryClient
      .invalidateQueries({ queryKey: ['me_api', 'topProducts_api', 'marketingBanner_api'] })
      .finally(() => resetToHomeNavStack(navigation));
  }

  return resetToHomeNavStack(navigation);
}

let lastToastTimestamp = 0;
const offset = { marginTop: 40 };

export function serverError(
  errorCodes: any,
  toast: any,
  t: (key: string) => string,
  navigation: any,
  clearState: () => void,
): false | void {
  if (errorCodes?.message === 'Network Error') {
    const now = Date.now();

    if (now - lastToastTimestamp > 5000) {
      lastToastTimestamp = now;
      showErrorToast(toast, t(`errorCodes.GG-REQ-BP-15`), `errorCodes.GG-REQ-BP-15`, offset);
    }

    return false;
  }

  if (
    Array.isArray(errorCodes) &&
    errorCodes.length > 0 &&
    (errorCodes[0] === 'GG-GEN-05' || errorCodes[0] === 'GG-REF-TKN-04')
  ) {
    clearState();
    gotBackToLogin(navigation);
    return;
  }

  if (!Array.isArray(errorCodes) || errorCodes.length === 0) return;

  showErrorToast(toast, t(`errorCodes.${errorCodes[0]}`), `errorCodes.${errorCodes[0]}`);
}

export function showErrorBottomSheet(
  setIsLoadingSMS: (val: boolean) => void,
  setIsLoadingWhatsapp: (val: boolean) => void,
  phoneNumber: string,
  error: string,
  setInfoModal: (val: any) => void,
): void {
  const authAmplitude = AMPLITUDE_CONSTANTS.authentication;
  setIsLoadingSMS(false);
  setIsLoadingWhatsapp(false);

  const e = authAmplitude.otp_failed;
  AmplitudeHelper.logEvent(e.name, {
    [e.attributes.phone_number]: formatPhoneNumber(phoneNumber),
    [e.attributes.failure_message]: AMPLITUDE_EVENT_SOURCES.login_error[error || 'GG-500'],
    [e.attributes.source]: AMPLITUDE_EVENT_SOURCES.login_page_source.login_page,
  });

  const key = LOGIN_INFO_SHEET_KEYS[error || 'GG-500'];
  const content = LOGIN_INFO_SHEET_CONTENT[key];

  setInfoModal({
    visible: true,
    data: returnBottomSheetContent(key, content, formatPhoneNumber(phoneNumber)),
    errorCode: key,
  });
}

export const singleFeatureEnabled = (
  featureList: { code: string; show: boolean }[],
  featureCode: string,
): { feature: { code: string; show: boolean } | null; show: boolean } => {
  const found = featureList?.find(f => f.code === featureCode);
  return {
    feature: found ?? null,
    show: found?.show ?? false,
  };
};

const multipleFeatureEnabled = (
  featureList: { code: string; show: boolean }[],
  featureCodes: string[],
): Record<string, { feature: { code: string; show: boolean } | null; show: boolean }> => {
  const resultObj: Record<string, { feature: { code: string; show: boolean } | null; show: boolean }> = {};
  featureCodes.forEach(code => {
    resultObj[code] = singleFeatureEnabled(featureList, code);
  });
  return resultObj;
};

export const isFeatureEnabled = (
  featureList: { code: string; show: boolean }[],
  featureCode: string | string[],
): any => {
  if (Array.isArray(featureCode)) return multipleFeatureEnabled(featureList, featureCode);
  return singleFeatureEnabled(featureList, featureCode);
};

export function validateInternetReachableState(
  setConnectionStatus: (status: boolean) => void,
): (state: { isConnected: boolean }) => void {
  return (state: { isConnected: boolean }) => {
    if (!state.isConnected) {
      setConnectionStatus(true);
    } else {
      setConnectionStatus(false);
    }
  };
}

export const PERMISSION_LIMIT = 3;

export function maskPhoneNumber(inputString: string): string {
  const { length } = inputString;
  if (length <= 4) return inputString;

  const maskedPart = '*'.repeat(length - 4);
  const visiblePart = inputString.slice(length - 4);
  return maskedPart + visiblePart;
}

type BankResponse = {
  accountNumber?: string;
  beneficiaryName?: string;
  bank?: {
    bankName?: string;
  };
  id?: string;
  nickname?: string;
  status?: string;
};

type UserBank = {
  accountNumber: string;
  beneficiaryName: string;
  bankName: string;
  id: string;
  nickName: string;
  status: string;
};

export const getUserBanksFromQuery = (
  data: BankResponse[] = [],
): { userBanks: UserBank[]; commonUserReadStatus: boolean } => {
  const userBanks: UserBank[] = [];
  const commonUserReadStatus = true;

  if (data.length > 0) {
    data.forEach(e => {
      const newBank: UserBank = {
        accountNumber: e?.accountNumber ?? '',
        beneficiaryName: e?.beneficiaryName ?? '',
        bankName: e?.bank?.bankName ?? '',
        id: e?.id ?? '',
        nickName: e?.nickname ?? '',
        status: e?.status ?? '',
      };
      userBanks.push(newBank);
    });
  }

  return {
    userBanks,
    commonUserReadStatus,
  };
};

export const formatUnderScoreLowerCase = (keyWord?: string): string | undefined => {
  if (!keyWord || keyWord.length === 0) {
    return keyWord;
  }
  return keyWord?.toLowerCase()?.replace(/ /g, '_') ?? '';
};

// Assuming AMPLITUDE_CONSTANTS and AmplitudeHelper are correctly typed elsewhere
export function logErrorOnCrash(error: Error, source?: string | null): void {
  const errorEvent = AMPLITUDE_CONSTANTS.errorPage.error_page_restart_button_clicked;
  const amplitudeProperties: Record<string, string> = {
    [errorEvent.attributes.event_triggers]: formatUnderScoreLowerCase(error?.message || (error as any)?.stack),
  };
  if (source !== null && source !== undefined) {
    amplitudeProperties.source = source;
  }

  AmplitudeHelper.logEvent(errorEvent.name, amplitudeProperties);
}

export const numberWithDots = (x?: string | number): any => x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');

export const isNotPoints = (selectedTxnAccount?: { accountTypeId?: number }): boolean =>
  selectedTxnAccount?.accountTypeId !== 1;

export function isEmptyObject(obj: Record<string, any>): boolean {
  return Object.keys(obj).length === 0;
}

export function getSelectedAccounts(
  selectedAcc: Record<string, any>,
  allAccounts: { accountTypeId: number }[],
  setSelectedTxnAccount: (acc: { accountTypeId: number }) => void,
): Record<string, any> {
  if (isEmptyObject(selectedAcc)) {
    const defaultAcc = allAccounts.find(e => e.accountTypeId === 0);
    if (defaultAcc) setSelectedTxnAccount(defaultAcc);
  }
  return selectedAcc;
}

// export function validatePin(code) {
//   if (code.length !== 6) return false;
//   let counter = 0;
//   let sequenceCounter = 1;
//   const uniqueObj = {};
//   let setAscDesc = -1;

//   for (let i = 0; i < code.length; i += 1) {
//     // all same pin validation
//     if (!uniqueObj[code[i]]) {
//       uniqueObj[code[i]] = true;
//       counter = 1;
//     } else if (uniqueObj[code[i]]) counter += 1;

//     if (counter === code.length) return false;

//     // sequence check
//     if (i + 1 < code.length) {
//       if (i === 0) {
//         if (code[i + 1] > code[i]) setAscDesc = 1;
//         else setAscDesc = -1;
//       }

//       if (setAscDesc === 1) {
//         if (code[i + 1] - code[i] === 1) sequenceCounter += 1;
//       } else if (setAscDesc === -1) {
//         if (code[i] - code[i + 1] === 1) sequenceCounter += 1;
//       } else sequenceCounter = 1;
//     }

//     if (sequenceCounter === code.length) return false;
//   }

//   return true;
// }
export function validatePin(code: string): boolean {
  if (code.length !== 6) return false;

  let isSamePin = true;
  let isSequence = true;

  for (let i = 0; i < code.length - 1; i += 1) {
    if (code[i] !== code[i + 1]) {
      isSamePin = false;
    }

    const diff = code.charCodeAt(i + 1) - code.charCodeAt(i);
    if (diff !== 1 && diff !== -1) {
      isSequence = false;
    }
  }

  return !(isSamePin || isSequence);
}

export function validateKTP(ktpNum: string): boolean {
  const pattern =
    /^(1[1-9]|21|[37][1-6]|5[1-3]|6[1-5]|[89][12])\d{2}\d{2}([04][1-9]|[1256][0-9]|[37][01])(0[1-9]|1[0-2])\d{2}\d{4}$/;
  return pattern.test(ktpNum);
}

export function filterRejected(payload: any[], isTransactional: boolean): any[] {
  const approvedList = [];
  const pendingList = [];
  const rejectedList = [];
  payload.forEach(e => {
    if (e.status === PHONE_ENROLLMENT_STATUS.APPROVED) approvedList.push(e);
    if (e.status === PHONE_ENROLLMENT_STATUS.PENDING) pendingList.push(e);
    if (!isTransactional && e.status === PHONE_ENROLLMENT_STATUS.REJECTED) {
      rejectedList.push(e);
    }
  });
  return approvedList.concat(pendingList, rejectedList);
}

export function onClickSecondaryPhonNoBottomSheet(
  setInfoModal: (value: any) => void,
  infoModal: any,
  deletePhoneNumber: (bankId: any) => void,
  currentSelectedBankId: any,
  BOTTONSHEET_PHONE_ENROLLMENT: any,
): void {
  setInfoModal({ ...infoModal, visible: false });
  if (infoModal.errorCode === BOTTONSHEET_PHONE_ENROLLMENT.PHONE_DELETE) {
    deletePhoneNumber(currentSelectedBankId);
  }
}

export function resetStackToCustomScreen(navigation: any, params: object, screenNameToReset: string): any {
  return navigation.dispatch((state: any) => {
    const { routes } = state;
    const indexOfPageToReset = routes.indexOf(routes.filter((item: any) => item.name === screenNameToReset)[0]);
    const splitRoutes = routes.slice(0, indexOfPageToReset + 1);
    return CommonActions.reset({
      ...state,
      routes: splitRoutes,
      index: splitRoutes.length - 1,
      ...params,
    });
  });
}

export function addPhnoCompleteCallBack(
  navigation: any,
  toast: any,
  data: any,
  t: (key: string) => string,
  params: any,
): any {
  if (data?.status === 'PENDING') {
    showWarningToast(toast, t('addPhone.phonePending'), 'phonePending');
  }
  if (data?.status === 'APPROVED') {
    showSuccessToast(toast, t('addPhone.phoneApproved'), 'phoneApproved');
  }
  const state = navigation.getState();
  const { routes } = state;
  const enrolledPhonesExists = routes.filter((item: any) => item.name === ScreenNames.ENROLLED_PHONES);
  if (enrolledPhonesExists.length > 0) {
    return resetStackToCustomScreen(navigation, params, ScreenNames.ENROLLED_PHONES);
  }
  const newRoute = [];
  newRoute.push(routes[0]);
  newRoute.push({ name: ScreenNames.ENROLLED_PHONES, params: 'ENROLLED_PHONES' });
  return navigation.dispatch(
    CommonActions.reset({
      ...state,
      routes: newRoute,
      index: 1,
      ...params,
    }),
  );
}

export function onErrPhoneNoCallbak(
  BOTTONSHEET_PHONE_ENROLLMENT: any,
  BOTTONSHEET_PHONE_CONTENT: any,
  setInfoModal: (state: any) => void,
  logEvent: (event: string) => void,
): (err: any) => void {
  return (err: any) => {
    if (err.message === 'PHONE_ALREADY_EXISTS') {
      setInfoModal(prevState => ({
        ...prevState,
        data: returnBottomSheetContent(
          BOTTONSHEET_PHONE_ENROLLMENT.DUPLICATE_ENTRY,
          BOTTONSHEET_PHONE_CONTENT[BOTTONSHEET_PHONE_ENROLLMENT.DUPLICATE_ENTRY],
        ),
        errorCode: BOTTONSHEET_PHONE_ENROLLMENT.DUPLICATE_ENTRY,
        visible: true,
      }));
      logEvent('number_duplicated');
    }

    if (err.message === 'REGISTERED_PHONE_LIMIT') {
      setInfoModal(prevState => ({
        ...prevState,
        data: returnBottomSheetContent(
          BOTTONSHEET_PHONE_ENROLLMENT.MAX_LIMIT,
          BOTTONSHEET_PHONE_CONTENT[BOTTONSHEET_PHONE_ENROLLMENT.MAX_LIMIT],
        ),
        errorCode: BOTTONSHEET_PHONE_ENROLLMENT.MAX_LIMIT,
        visible: true,
      }));

      logEvent('max_reached');
    }
  };
}

export const getReferralEncryption = (code: string): string =>
  code
    .split('')
    .map(item => item.charCodeAt(0))
    .join('');

export function changeAmountWithFormatting(
  currencySeperatorRegEx: string,
  setDisableWithdrawButton: (val: boolean) => void,
  setInputAmountValue: (val: string) => void,
): (val: string) => void {
  return val => {
    const converted = val.replace(new RegExp(currencySeperatorRegEx, 'g'), '');

    if (isValidNumberWithDot(converted)) {
      setDisableWithdrawButton(false);
      setInputAmountValue(converted);
    } else {
      setDisableWithdrawButton(true);
    }
  };
}

export function shouldShowRightArrowOrTickForPhone(item: any, bpPhone: any): JSX.Element {
  if (item.mobileNumber === bpPhone.mobileNumber) {
    return <Ionicons name="checkmark" size={18} color={colors.primary.carnation} />;
  }
  return <RightArrow />;
}

export function showStatusViewForPhone(
  item: any,
  t: (key: string) => string,
  isTransact: boolean,
  bpPhone: any,
): JSX.Element | null {
  if (item?.status?.toUpperCase() === 'PENDING') {
    return (
      <View style={appStyles.pendingContainer}>
        <Text variant="xsm-bold" style={appStyles.pendingText}>
          {t('phoneTransfer.phoneVerify')}
        </Text>
      </View>
    );
  }
  if (item?.status?.toUpperCase() === 'APPROVED' && isTransact) {
    return shouldShowRightArrowOrTickForPhone(item, bpPhone);
  }
  if (item?.status?.toUpperCase() === 'REJECTED') {
    return (
      <TouchableOpacity>
        <View style={appStyles.rejectedContainer}>
          <View style={appStyles.rejectedTextContainer}>
            <Text variant="xsm-bold" style={appStyles.rejectedText}>
              {t('phoneTransfer.phoneRejected')}
            </Text>
          </View>
        </View>
      </TouchableOpacity>
    );
  }
  return null;
}

export function shouldShowRightArrowOrTickForBank(item: any, bank: any): JSX.Element {
  if (bank && item.accountNumber === bank.accountNumber) {
    return <Ionicons name="checkmark" size={18} color={colors.primary.carnation} />;
  }
  return <RightArrow />;
}

export function showStatusViewForBank(
  item: any,
  t: (key: string) => string,
  isTransact: boolean,
  bankForTransfer: any,
): JSX.Element | null {
  if (item?.status?.toUpperCase() === 'PENDING') {
    return (
      <View style={appStyles.pendingContainer}>
        <Text variant="xsm-bold" style={appStyles.pendingText}>
          {t('phoneTransfer.phoneVerify')}
        </Text>
      </View>
    );
  }
  if (item?.status?.toUpperCase() === 'APPROVED' && isTransact) {
    return shouldShowRightArrowOrTickForBank(item, bankForTransfer);
  }
  if (item?.status?.toUpperCase() === 'REJECTED') {
    return (
      <TouchableOpacity>
        <View style={appStyles.rejectedContainer}>
          <View style={appStyles.rejectedTextContainer}>
            <Text variant="xsm-bold" style={appStyles.rejectedText}>
              {t('phoneTransfer.phoneRejected')}
            </Text>
          </View>
        </View>
      </TouchableOpacity>
    );
  }
  return null;
}

function getNpmVersion(): string {
  return packageJson.version;
}

export function getVersionCode(): number {
  const [major, minor, patch] = getNpmVersion().split('.');
  const base = 1000000000;
  const versionMajorTransposed = parseInt(major, 10) * 10000;
  const versionMinorTransposed = parseInt(minor, 10) * 100;
  const versionPatchTransposed = parseInt(patch, 10);
  return base + versionMajorTransposed + versionMinorTransposed + versionPatchTransposed;
}

export function getReleaseName(): string {
  const buildVersionCode = getVersionCode();
  return `${config.ENVIRONMENT}@${buildVersionCode}`;
}

export const getAppUpdateStatus = (list: any[], currentSemverSplit: string[]): string => {
  let mandatory = 0;
  let nonMandatory = 0;
  list.forEach(e => {
    const splitSemver = e.version_number.split('.');
    for (let index = 0; index < splitSemver.length; index += 1) {
      const a = parseInt(splitSemver[index], 10);
      let b: number | null = null;
      if (currentSemverSplit?.[index] && !isNaN(+currentSemverSplit[index])) {
        b = parseInt(currentSemverSplit[index], 10);
      }

      if (a > (b ?? 0)) {
        if (e.force_update) {
          mandatory += 1;
        } else if (e.recommended_update) {
          nonMandatory += 1;
        }
      } else if (a < (b ?? 0) && (index === 0 || index === 1)) {
        break;
      }
    }
  });

  if (mandatory > 0) {
    return UPDATE_TYPE.MANDATORY;
  }
  if (nonMandatory > 0) {
    return UPDATE_TYPE.OPTIONAL;
  }
  return UPDATE_TYPE.NO_UPDATE;
};

export const PLAYSTORE_URL = 'https://play.google.com/store/apps/details?id=com.gajigesanative&hl=en';

export const SHARE_LINK_ANDROID = `${config.BASE_PATH}/go?link=gajigesa://gajigesanative.page.link/share`;

type SuccessPageBanner = {
  redirectionType?: string;
  redirectTo?: string;
};

type NavigationType = any; // Replace with actual type from your navigation lib
type LogParamsType = Record<string, any>;

export function onRedirectionHandler(
  successPageBanners: SuccessPageBanner[],
  navigation: NavigationType,
  eventName: string,
  logParams: LogParamsType,
) {
  return () => {
    const { redirectionType = '', redirectTo = '' } = successPageBanners[0];
    AmplitudeHelper.logEvent(eventName, logParams);

    if (redirectionType === REDIRECTION_TYPES.SCREEN_NAMES) {
      return resetToNavStack(navigation, redirectTo);
    }

    if (redirectionType === REDIRECTION_TYPES.EXTERNAL_LINK) {
      return Linking.openURL(redirectTo);
    }

    if (redirectionType === REDIRECTION_TYPES.DEEPLINK) {
      const result = Linking.canOpenURL(SHARE_LINK_ANDROID);
      if (result) return Linking.openURL(SHARE_LINK_ANDROID);
    }

    return null;
  };
}

export const getAmplitudeYesNo = (value: any): string => (value ? 'yes' : 'no');

export const getAmplitudeKeyValue = (refEvent: string, arrObj: any[] = []): Record<string, string> => {
  if (!arrObj) return {};
  const filteredEventsObj = arrObj.filter(eo => eo?.event?.includes(refEvent));
  const solution: Record<string, string> = {};
  filteredEventsObj.forEach(item => {
    solution[item.key] = item.value;
  });
  return solution;
};

export function shouldShowErrorForAccountInput(sErrorLabel?: string) {
  if (sErrorLabel?.length > 0) {
    return (
      <Text testID="textErrorMessage" style={appStyles.marginVertical(4)} variant="sm-normal" color={colors.error.rose}>
        {sErrorLabel}
      </Text>
    );
  }
  return null;
}

export function getFailureMessage(errorCode: string): string {
  if (errorCode === 'GG-RF-01') return 'invalid_code';
  if (errorCode === 'GG-RF-02') return 'not_first_transaction';
  if (errorCode === 'GG-RF-03') return 'user_used_their_own_code';
  if (errorCode === 'GG-RF-04') return 'invalid_code_not_exist';
  if (errorCode === 'GG-RF-05' || errorCode === 'GG-RF-06') return 'invalid_code_referral_not_active';
  return 'system_error';
}

export function showErrorOnAPI(
  isError: boolean,
  error: string[],
  toast: any,
  t: (key: string) => string,
  navigation: any,
  setInitialData: () => void,
  referralCode: string,
  pageSource: string,
): void {
  if (isError && error) {
    const e = AMPLITUDE_CONSTANTS.redeemptionFlow.referral_code_failed;
    AmplitudeHelper.logEvent(e.name, {
      [e.attributes.referral_code]: referralCode ?? '',
      [e.attributes.source]: pageSource,
      [e.attributes.failure_message]: getFailureMessage(error[0]),
    });
    serverError(error, toast, t, navigation, setInitialData);
  }
}
